import { useMantineTheme, Group, Button, Loader } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useCallback, useEffect, useState } from "react";
import {
  UpdatePolicyInput,
  useGetEditorLinkLazyQuery,
  useGetPolicyLazyQuery,
  useUpdatePolicyMutation,
} from "src/generated/graphql";
import { useParams } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import { useDisclosure } from "@mantine/hooks";
import { policyFormSchema } from "../PolicyList/PolicyForm";
import { EMPLOYEE_ROLE_OBJ, MESSAGES } from "src/utils/constants";
import FormCard from "src/components/FormCard";
import { flushSync } from "react-dom";
import { GetPolicy } from "src/api/graphql/queries/Policy";
import { useModals } from "@mantine/modals";
import DocumentModal from "./DocumentModal";
import useWritePermission from "src/hooks/usePermission";

const initialValues: UpdatePolicyInput = {
  name: "",
  fileB64: "",
};

const PolicyDetail = () => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgPolicies);
  const modals = useModals();
  const { policyId } = useParams<{ policyId: string }>();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const [isDraft, setisDraft] = useState(false);
  const [isLoaded, setisLoaded] = useState(false);
  const theme = useMantineTheme();
  const [getPolicy, { data: policyData }] = useGetPolicyLazyQuery({
    onCompleted: () => {
      resetFormState();
    },
  });

  const [getEditorLink, { data: editorLink, loading }] =
    useGetEditorLinkLazyQuery();

  const [updatePolicy, { loading: UpdateLoading }] = useUpdatePolicyMutation({
    onCompleted: () => {
      toggleFormActive();
      flushSync(() => {
        setisLoaded(false);
      });
      form.resetDirty();
    },
    refetchQueries: [{ query: GetPolicy, variables: { id: policyId } }],
  });

  useEffect(() => {
    policyId &&
      getPolicy({
        variables: { id: policyId },
      });
  }, [getPolicy, policyId]);

  const form = useForm<UpdatePolicyInput>({
    initialValues,
    validate: yupResolver(policyFormSchema),
  });
  const handleUpdate = async (values: UpdatePolicyInput, isUpload: Boolean) => {
    if (!policyId) return;
    try {
      await updatePolicy({
        variables: {
          data: isUpload ? { fileB64: values.fileB64 } : { name: values.name },
          id: policyId,
        },
      });
    } catch (e: any) {
      console.error("Error: ", e.response);
    }
  };

  const resetFormState = () => {
    const { name } = policyData?.policyOne || {};
    let _data = {
      name,
    };
    form.setValues(_data);
    form.resetDirty(_data);
    setisLoaded(true);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  useEffect(() => {
    if (policyData?.policyOne.documentId) {
      getEditorLink({
        variables: {
          id: policyData?.policyOne.documentId,
        },
        nextFetchPolicy: "network-only",
      });
    }
  }, [policyData]);

  const isFormUpdated = form.isDirty() && form.isTouched();

  const handleChangeModal = (openEditModal?: boolean) => {
    modals.closeAll();
    // if (openEditModal) {
    //   handleModal(openEditModal);
    // }
  };

  const handleModal = useCallback(
    (isModify: Boolean) => {
      if (!loading && editorLink) {
        modals.openModal({
          closeOnClickOutside: false,
          title: `${isModify ? "Modify" : "Upload"} Document`,
          children: (
            <DocumentModal
              isModify={isModify}
              editorLink={isModify && editorLink.getEditorLink}
              closeModal={handleChangeModal}
              policyId={policyId}
            />
          ),
          styles: {
            modal: {
              width: "auto",
              minWidth: isModify ? "90vw" : "50vw",
            },
            body: {
              minHeight: isModify ? "70vh" : "fit-content",
            },
          },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }
    },
    [editorLink, policyId, loading]
  );

  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={UpdateLoading}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={handleCancel}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => {
          handleUpdate(values, false);
        })}
        isFormActive={isFormActive}
        mb={25}
      >
        <TextInput
          disabled={isFormActive}
          withAsterisk
          label="Name"
          {...form.getInputProps("name")}
        />
      </FormCard>

      <FormCard title={"Update Document"} mb={25}>
        {loading ? (
          <Loader size={80} />
        ) : (
          <Group position="center" mt="lg" mb="lg" pl={"lg"} pr={"lg"}>
            <Button
              radius="xl"
              compact
              variant="outline"
              onClick={() => handleModal(false)}
              style={{
                padding: "0px 50px",
                borderColor: !isDraft ? theme.colors.dark[8] : "#DDD",
                borderWidth: !isDraft ? 2 : 1,
                backgroundColor: theme.colors.primary[4],
              }}
              disabled={!isAllowWrite}
            >
              Upload New PDF
            </Button>
            OR
            <Button
              radius="xl"
              compact
              variant="outline"
              onClick={() => handleModal(true)}
              style={{
                padding: "0px 50px",
                borderColor: !isDraft ? theme.colors.dark[8] : "#DDD",
                borderWidth: !isDraft ? 2 : 1,
                backgroundColor: theme.colors.primary[4],
              }}
              disabled={!isAllowWrite}
            >
              Modify Document
            </Button>
          </Group>
        )}
      </FormCard>
    </>
  );
};

export default PolicyDetail;
